import yup from '~/core/validation';
import { useForm } from 'vee-validate';

// Only the phone is required. The consent is too, because the bank analysis
// shares the data with partners.
export const creditContactSchema = yup.object({
	name: yup.string().trim().optional(),
	phone: yup
		.string()
		.phone('Informe um WhatsApp válido.')
		.required('Informe seu WhatsApp.'),
	email: yup.string().email('Informe um e-mail válido.').optional(),
	privacyAccepted: yup
		.boolean()
		.oneOf([true], 'Autorize o uso dos dados para continuar.')
		.required('Autorize o uso dos dados para continuar.')
});

export type CreditContactValues = yup.InferType<typeof creditContactSchema>;

const FIELD_OPTIONS = {
	validateOnBlur: true,
	validateOnChange: true,
	validateOnInput: false,
	validateOnModelUpdate: false
} as const;

export const CREDIT_CONTACT_STATE_KEY = 'credit-contact';

export function useCreditContactForm() {
	const { user } = useUserSession();
	// The step unmounts on "Voltar"; the typed values come back with it.
	const saved = useState<CreditContactValues>(CREDIT_CONTACT_STATE_KEY, () => ({
		name: '',
		phone: '',
		email: '',
		privacyAccepted: false
	}));
	const { defineField, errors, validate, values } =
		useForm<CreditContactValues>({
			validationSchema: creditContactSchema,
			initialValues: { ...saved.value }
		});

	watch(values, current => (saved.value = { ...current }), { deep: true });

	const [name, nameAttrs] = defineField('name', FIELD_OPTIONS);
	const [phone, phoneAttrs] = defineField('phone', FIELD_OPTIONS);
	const [email, emailAttrs] = defineField('email', FIELD_OPTIONS);
	const [privacyAccepted, privacyAcceptedAttrs] = defineField(
		'privacyAccepted',
		FIELD_OPTIONS
	);

	// Only empty fields are filled, so nothing the user typed is overwritten.
	function fillFromUser() {
		const currentUser = user.value;
		if (!currentUser) return;
		if (!name.value && currentUser.name) name.value = currentUser.name;
		if (!email.value && currentUser.email) email.value = currentUser.email;
		if (!phone.value && currentUser.phoneNumber) {
			phone.value = currentUser.phoneNumber;
		}
	}

	watch(user, fillFromUser, { immediate: true });

	return {
		name,
		nameAttrs,
		phone,
		phoneAttrs,
		email,
		emailAttrs,
		privacyAccepted,
		privacyAcceptedAttrs,
		errors,
		validate
	};
}
